// Shared Supabase client. URL + anon key load from .env at build time via
// Vite's `import.meta.env.VITE_*`. The .env file lives outside the repo
// (.gitignore'd) — see .env.example for the expected shape. RLS policies on
// the Supabase project are the actual security boundary.

import { createClient, type Session, type User } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!SUPABASE_URL || !SUPABASE_ANON) {
  throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY — copy web/.env.example to web/.env and fill in the values.');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
});

let _user: User | null = null;
let _ready: Promise<User | null>;

export function whoAmI(): Promise<User | null> {
  if (!_ready) {
    _ready = supabase.auth.getSession().then(({ data }) => {
      _user = data.session?.user ?? null;
      supabase.auth.onAuthStateChange((_evt, session: Session | null) => {
        _user = session?.user ?? null;
      });
      return _user;
    });
  }
  return _ready;
}

export function currentUserSync(): User | null { return _user; }

const K_LOCAL_ONLY = 'mystack:local-only-mode:v1';

/** True when the user has explicitly chosen local-only (no cloud sync). */
export function isLocalOnly(): boolean {
  return localStorage.getItem(K_LOCAL_ONLY) === 'true';
}
export function setLocalOnly(v: boolean): void {
  if (v) localStorage.setItem(K_LOCAL_ONLY, 'true');
  else localStorage.removeItem(K_LOCAL_ONLY);
}

/** True when we should read/write to Supabase. False = pure localStorage. */
export async function isCloudMode(): Promise<boolean> {
  if (isLocalOnly()) return false;
  const u = await whoAmI();
  return !!u;
}
